import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../lib/LanguageContext";
import { Menu, X, Languages } from "lucide-react";

const Navbar = () => {
  const { lang, toggleLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/facilities", label: t.nav.facilities },
    { to: "/activities", label: t.nav.activities },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/achievements", label: t.nav.achievements },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#FAFAF7]/95 backdrop-blur-xl border-b border-school shadow-sm" : "bg-[#FAFAF7]/80 backdrop-blur-md"
      }`}
    >
      <div className="container-school flex items-center justify-between h-[72px]">
        {/* Brand - text only logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center min-w-0 group">
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-[15px] md:text-base lg:text-lg leading-tight text-school-primary truncate group-hover:text-[var(--school-primary-dark)] transition-colors">
              {t.schoolName}
            </div>
            <div className="font-body text-[11px] md:text-xs text-school-muted truncate mt-0.5">
              {t.schoolTagline}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg font-heading font-semibold text-[14px] transition-all ${
                  isActive
                    ? "text-school-primary bg-[#F0F0EA]"
                    : "text-school-text hover:text-school-primary hover:bg-[#F0F0EA]/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="lang-switcher"
            aria-label="Toggle language"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-school bg-white text-sm font-semibold text-school-primary hover:bg-school-primary hover:text-white hover:border-school-primary transition-all"
          >
            <Languages className="w-4 h-4" />
            <span>{lang === "mr" ? "English" : "मराठी"}</span>
          </button>

          <Link
            to="/contact#enquiry"
            data-testid="nav-enquire-btn"
            className="hidden md:inline-flex btn-secondary !py-2 !px-4 !rounded-full text-sm"
          >
            {t.nav.enquire}
          </Link>

          <button
            className="xl:hidden p-2 rounded-lg hover:bg-[#F0F0EA] transition-colors"
            onClick={() => setOpen((p) => !p)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="xl:hidden border-t border-school bg-[#FAFAF7]" data-testid="mobile-nav">
          <nav className="container-school py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg font-heading font-semibold transition-all ${
                    isActive ? "bg-school-primary text-white" : "text-school-text hover:bg-[#F0F0EA]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact#enquiry"
              data-testid="mobile-enquire-btn"
              className="btn-secondary mt-3 w-full"
            >
              {t.nav.enquire}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
